import { ChangeEvent, FormEvent, useState, useCallback } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';

import { ConfirmAuthenticatorSerialized, ConfirmRecoveryCodeSerialized } from '@playq/octopus2-auth';

type UseMFAInputParams = {
  withRecovery?: boolean;
  onSubmit: (params: ConfirmAuthenticatorSerialized | ConfirmRecoveryCodeSerialized) => void;
  onClose?: () => void;
};

export const useMFAInput = (params: UseMFAInputParams) => {
  const { withRecovery, onSubmit, onClose } = params;

  const [token, setToken] = useState<string>('');
  const [pass, setPass] = useState('');
  const [isRecoveryCode, setIsRecoveryCode] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPass(e.target.value);
  };

  const toggleIsRecoveryCode = () => {
    setIsRecoveryCode((prev) => !prev);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isRecoveryCode) {
      onSubmit({ recoveryCode: pass, token });
    } else {
      onSubmit({ pass, token });
    }
    setToken('');
  };

  const handleOpen = useCallback((newToken: string) => {
    setToken(newToken);
  }, []);

  const handleClear = () => {
    setToken('');
    setPass('');
    setIsRecoveryCode(false);
  };

  const handleClose = () => {
    handleClear();
    onClose?.();
  };

  const node = (
    <Dialog open={!!token} maxWidth='xs' onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Enter the code from Google Authenticator{withRecovery && ' or recovery code'}</DialogTitle>
        <DialogContent>
          <Stack direction='row' alignItems='center' gap={2}>
            <TextField value={pass} size='small' autoFocus={true} onChange={handleChange} />
            {withRecovery && (
              <FormControlLabel
                control={<Checkbox checked={isRecoveryCode} onChange={toggleIsRecoveryCode} />}
                label='Confirm with recovery code'
                sx={{ whiteSpace: 'nowrap' }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size='large' type='submit'>
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );

  return [node, handleOpen] as const;
};
